import { HTTP_TYPES } from '@/lib/enums';
import api from '@/api/api';
import type {
    DepotApiRouteResponse,
    DepotProfileSiteAssignmentPayload,
    DepotProfileSiteAssignmentResponse,
    DepotProfileSiteRemovalPayload,
    OwnershipScope,
    SitesSearchRequest
} from './types/companies-sites.types';
import { ownershipScopeBody } from './helpers/companies-sites.helpers';

export type {
    OwnershipScope,
    SiteSearchRow,
    SitesSearchRequest,
    SitesSearchResponse
} from './types/companies-sites.types';

const API_VERSION = 'v2';

const response = {
    /**
     * Server-side paginated search across every site in every company.
     * POST /company/sites/search
     *
     * Answers "nothing matched" with a 404 carrying the same envelope shape as a
     * 200 and an empty `data` array, so callers must read the 404 body rather
     * than treating the status alone as a failure.
     */
    async postSitesSearch(payload: SitesSearchRequest): Promise<any> {
        return await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/search`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async getSitesCSV(): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/csv`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async getSitesWithProfilesCSV(): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/profiles/csv`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async postReassignSite(payload: any): Promise<any> {
        // site_uuid: guid
        // target_company_uuid: guid

        return await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/reassign-site`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async postUpdateSectorMantleConfig(sector_uuid: string, payload: any): Promise<any> {
        // mantle_uuid: guid

        return await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sectors/${sector_uuid}/update-mantle-config`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async postUpdateSitesGooglePlaceDetails(payload: any): Promise<any> {
        // company_uuid: guid

        return await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/update-google-place-details`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async getGooglePlaceDetails(place_id: string): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/place-id/${place_id}`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async getSiteDetailsFromProfileUuid(profile_uuid: string): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/profile/${profile_uuid}`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async getSiteSectorInformation(profile_uuid: string): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/site-sector/${profile_uuid}`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async postSyncAllSitesSectors(payload: any): Promise<any> {
        // offset: int
        // limit: int

        return await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/sync-site-sectors`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async getTotalSitesCount(): Promise<any> {
        return await api(
            HTTP_TYPES.GET,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/count`,
            undefined,
            undefined,
            'application/json',
            API_VERSION
        );
    },
    async postUpdateSitesNaming(
        payload: { company_uuid?: string },
        onChunk: (chunk: string) => void
    ): Promise<void> {
        await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/update-sites-naming`,
            payload,
            undefined,
            'application/json',
            API_VERSION,
            {},
            onChunk
        );
    },
    /**
     * Announce current site-and-company ownership for the profiles in scope,
     * streaming progress as it goes.
     *
     * The body and every field within it are optional, and the three selectors are
     * mutually exclusive. Omitting the body entirely covers the whole estate, which
     * is why `scope` is optional here and why absence is passed through as no body
     * rather than as an empty object.
     *
     * The messages state current ownership rather than deltas, so a failed or
     * cancelled run is safe to repeat — a partial run just leaves the rest
     * unannounced.
     *
     * The response is a streamed `text/plain` body of `data: ...` lines. It is NOT
     * `text/event-stream` despite those SSE-style prefixes, so `fetchEventSource`
     * cannot be used: its `onopen` validates the content type and fails the connect
     * with an error naming the status code, which points nowhere near the cause.
     * `onChunk` takes the raw decoded bytes instead, as the neighbouring streams do.
     *
     * A profile found on more than one site breaks the one-site-per-profile
     * invariant. No message is sent for it; the stream reports only how many were
     * skipped, and the identities go to Slack, capped at 20 per run.
     */
    async postSyncProfileOwnership(
        scope: OwnershipScope | undefined,
        onChunk: (chunk: string) => void,
        signal?: AbortSignal
    ): Promise<void> {
        await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/sync-profile-ownership`,
            ownershipScopeBody(scope),
            undefined,
            'application/json',
            API_VERSION,
            {},
            onChunk,
            signal
        );
    },
    async postAssignProfileToSite(
        payload: DepotProfileSiteAssignmentPayload
    ): Promise<DepotApiRouteResponse<DepotProfileSiteAssignmentResponse>> {
        return (await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/reassign-profile`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        )) as DepotApiRouteResponse<DepotProfileSiteAssignmentResponse>;
    },
    /**
     * Detach a profile from one site, leaving it attached to no site at all.
     *
     * Two behaviours a caller cannot infer from the request shape:
     *
     * 1. This is NOT idempotent. A second removal of the same profile answers 404
     *    rather than repeating the success, so a retry after a successful removal
     *    reports a failure. Callers must treat 404 as terminal, not as something
     *    to try again.
     * 2. No client webhook and no profile ownership message are sent, by design.
     *    Anything caching where this profile lives keeps the old site until it is
     *    told some other way, and nothing here tells it.
     *
     * The owning company is resolved from `site_uuid`, so no company identifier
     * is sent. Only the named site is touched: a profile held by more than one
     * site stays on the others.
     */
    async postRemoveProfileFromSite(payload: DepotProfileSiteRemovalPayload): Promise<any> {
        return await api(
            HTTP_TYPES.POST,
            `${import.meta.env.VITE_DEPOT_API_COMPANIES}/sites/remove-profile`,
            payload,
            undefined,
            'application/json',
            API_VERSION
        );
    }
};

export default response;
